<template>
    <main class="voice-designer-page">
        <section class="designer-hero">
            <div>
                <p class="eyebrow">Voice studio</p>
                <h1>Voice Designer</h1>
                <p class="hero-copy">Create, test, and save production-ready custom voices in one focused workspace.</p>
            </div>
            <button class="primary-action" type="button" @click="startChooseBaseVoice">
                <span aria-hidden="true">+</span>
                Design New Voice
            </button>
        </section>

        <section class="stats-grid" aria-label="Voice designer overview">
            <article class="stat-card">
                <span>Designed voices</span>
                <strong>{{ userVoices.length }}</strong>
                <p>Saved in your voice library</p>
            </article>
            <article class="stat-card">
                <span>Fastest start</span>
                <strong>3 ways</strong>
                <p>Collection, upload, or record</p>
            </article>
        </section>

        <div class="designer-grid">
            <section class="voice-library-panel">
                <div class="panel-header">
                    <div>
                        <div class="section-title" role="heading" aria-level="2">Saved voices</div>
                        <p>Preview, rename, tag, or reuse a saved voice as a base.</p>
                    </div>
                </div>
                <VGUserVoiceExplorerModal title="All Your Designed Voices" placeholder="Search Your Designed Voices"
                    label-key="voice_name" />
            </section>

            <aside class="start-panel">
                <div class="section-title" role="heading" aria-level="2">Start from</div>
                <p class="start-copy">Choose a source first, then shape the performance with a prompt.</p>
                <button class="quick-start" type="button" @click="startChooseBaseVoice">
                    <span class="quick-icon">V</span>
                    <span>
                        <strong>Voice collection</strong>
                        <small>Pick a built-in or saved voice.</small>
                    </span>
                </button>
                <button class="quick-start" type="button" @click="startChooseBaseVoice">
                    <span class="quick-icon">U</span>
                    <span>
                        <strong>Upload audio</strong>
                        <small>Use a clean source sample.</small>
                    </span>
                </button>
                <button class="quick-start" type="button" @click="startChooseBaseVoice">
                    <span class="quick-icon">R</span>
                    <span>
                        <strong>Record now</strong>
                        <small>Capture audio in the workflow.</small>
                    </span>
                </button>
                <div class="sample-wave-card">
                    <div class="mini-wave" aria-hidden="true">
                        <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                    </div>
                    <span>Every new design keeps preview and save actions visible.</span>
                </div>
            </aside>
        </div>
    </main>
</template>

<script>
import { ref, onMounted } from 'vue';
import { getUserVoicesByUserId } from '@/api/services'
import { useRouter } from 'vue-router';
import VGUserVoiceExplorerModal from '@/components/common/VGUserVoiceExplorerModal.vue';
export default {
    name: 'VoiceDesignerView',
    components: {
        VGUserVoiceExplorerModal,
    },
    setup() {
        const router = useRouter();
        const userVoices = ref([]);
        async function loadVoices() {
            try {
                userVoices.value = await getUserVoicesByUserId();
                console.log(userVoices.value)
            } catch (err) {
                console.error('Failed to load voices:', err);
            }
        }
        function startChooseBaseVoice() {
            router.push({
                name: 'choose-base-voice',
            });
        }
        onMounted(async () => {
            await loadVoices();
        });
        return {
            userVoices,
            startChooseBaseVoice,
        }
    },
};
</script>

<style scoped>
.voice-designer-page {
    min-height: 100%;
    padding: 24px 28px 36px;
    color: #111827;
    background:
        linear-gradient(120deg, rgba(124, 58, 237, 0.08), transparent 28%),
        linear-gradient(240deg, rgba(37, 99, 235, 0.07), transparent 30%),
        #f7f8fc;
}

.designer-hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 22px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(249, 247, 255, 0.92)),
        radial-gradient(circle at 92% 12%, rgba(124, 58, 237, 0.12), transparent 30%);
    box-shadow: 0 22px 70px rgba(17, 24, 39, 0.08);
}

.eyebrow {
    margin: 0 0 6px;
    color: #7c3aed;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.designer-hero h1 {
    margin: 0;
    font-size: 28px;
    line-height: 1.15;
}

.hero-copy,
.panel-header p,
.start-copy,
.quick-start small,
.sample-wave-card span,
.stat-card p {
    color: #5f6b7d;
}

.hero-copy {
    margin: 6px 0 0;
    line-height: 1.5;
}

.primary-action {
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 14px;
    border: 0;
    border-radius: 7px;
    color: white;
    background: linear-gradient(135deg, #7c3aed, #6d5dfc);
    box-shadow: 0 12px 28px rgba(109, 93, 252, 0.18);
    cursor: pointer;
    transition: background 180ms ease, transform 180ms ease;
}

.primary-action:hover {
    transform: translateY(-1px);
    background: linear-gradient(135deg, #6d28d9, #5b50e8);
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    margin-top: 16px;
}

.stat-card,
.voice-library-panel,
.start-panel {
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 10px 34px rgba(17, 24, 39, 0.06);
}

.stat-card {
    padding: 15px;
}

.stat-card span {
    color: #5f6b7d;
    font-size: 12px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.stat-card strong {
    display: block;
    margin-top: 8px;
    font-size: 25px;
}

.stat-card p {
    margin: 3px 0 0;
    font-size: 13px;
}

.designer-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 20px;
    margin-top: 16px;
    align-items: start;
}

.voice-library-panel,
.start-panel {
    padding: 18px;
}

.voice-library-panel :deep(.pm-modal),
.voice-library-panel :deep(.pm-header),
.voice-library-panel :deep(.pm-header > div),
.voice-library-panel :deep(.pm-eyebrow),
.voice-library-panel :deep(.pm-header h3),
.voice-library-panel :deep(.vc-name-tags),
.voice-library-panel :deep(.vc-name-row),
.voice-library-panel :deep(.vc-tags),
.voice-library-panel :deep(.vc-instruct) {
    text-align: left;
}

.voice-library-panel :deep(.pm-header) {
    align-items: flex-start;
}

.voice-library-panel :deep(.vc-name-row) {
    justify-content: flex-start;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.section-title {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    line-height: 1.2;
    text-align: left;
}

.panel-header p,
.start-copy {
    margin: 6px 0 0;
    line-height: 1.5;
}

.start-panel {
    position: sticky;
    top: 24px;
    display: grid;
    gap: 12px;
}

.quick-start {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr);
    gap: 10px;
    padding: 12px;
    border: 1px solid #dfe5ef;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.86);
    text-align: left;
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.quick-start:hover {
    border-color: #c4b5fd;
    background: #fbfaff;
    transform: translateY(-1px);
}

.quick-icon {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: 7px;
    background: #f3f0ff;
    color: #7c3aed;
    font-weight: 700;
}

.quick-start strong,
.quick-start small {
    display: block;
}

.quick-start small {
    margin-top: 2px;
    line-height: 1.35;
}

.sample-wave-card {
    display: grid;
    gap: 8px;
    padding: 12px;
    border: 1px solid #dfe5ef;
    border-radius: 7px;
    background: #f8fafc;
}

.mini-wave {
    height: 34px;
    display: flex;
    align-items: center;
    gap: 3px;
}

.mini-wave i {
    display: block;
    width: 4px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(124, 58, 237, 0.82), rgba(37, 99, 235, 0.4));
}

.mini-wave i:nth-child(1) { height: 9px; }
.mini-wave i:nth-child(2) { height: 18px; }
.mini-wave i:nth-child(3) { height: 13px; }
.mini-wave i:nth-child(4) { height: 25px; }
.mini-wave i:nth-child(5) { height: 17px; }
.mini-wave i:nth-child(6) { height: 29px; }
.mini-wave i:nth-child(7) { height: 12px; }
.mini-wave i:nth-child(8) { height: 22px; }
.mini-wave i:nth-child(9) { height: 15px; }
.mini-wave i:nth-child(10) { height: 26px; }

button:focus-visible {
    outline: 3px solid rgba(124, 58, 237, 0.24);
    outline-offset: 2px;
}

@media (max-width: 1120px) {
    .designer-grid {
        grid-template-columns: 1fr;
    }

    .start-panel {
        position: static;
    }
}

@media (max-width: 760px) {
    .voice-designer-page {
        padding: 16px;
    }

    .designer-hero {
        align-items: stretch;
        flex-direction: column;
    }

    .stats-grid {
        grid-template-columns: 1fr;
    }
}
</style>
